// Copyright (c) StrangeBee 2023

import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { ExtractProps } from '@Molecules/Details/Details';
import { FormatDate } from '@Molecules/Format';
import React, { ReactElement } from 'react';
import { CaseResolutionStatus, CaseStatus, CaseOutput, AlertOutput } from 'thehive-sdk';
import { Space } from 'antd';
import { Link } from 'react-router-dom';
import THTag from '@Atoms/THTag/THTag';
import { classList } from '@Helpers/className';
import { ICONS } from '@Constants/iconsApp';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { cutString } from '@Helpers/string';
import RouteBuilder from '@Services/RouteBuilder';

interface ExtractDataProps {
    locale: (...args: any[]) => any;
}

export const extractData = ({ locale }: ExtractDataProps): Record<string, ExtractProps> => ({
    '#computeTitle': {
        name: '',
        render: ({ title, path, icon }: { title: string; path: string; icon: ReactElement }): ReactElement => (
            <div className="result-card__title">
                <Space>
                    {icon}
                    <Link to={path}>{title}</Link>
                </Space>
            </div>
        ),
    },
    '#computeDate': {
        name: '',
        render: ({ date, _createdBy }: { date: number; _createdBy: string }): ReactElement => (
            <div className="result-card__date">
                <FormatDate icon date={date} />
                {_createdBy && <AssigneeInfo assignee={_createdBy} />}
            </div>
        ),
    },
    '#computeStatus': {
        name: '',
        isForce: false,
        render: ({
            status,
            resolutionStatus,
            startDate,
            endDate,
            _createdAt,
        }: {
            status: CaseStatus;
            resolutionStatus: CaseResolutionStatus;
            startDate: number;
            endDate: number;
            _createdAt: number;
        }): ReactElement => (
            <div className="case-result-card__status-container">
                <div
                    className={classList('case-result-card__status', {
                        open: status === CaseStatus.OPEN,
                        closed: status === CaseStatus.RESOLVED,
                    })}
                >
                    <THTag
                        className="case-result-card__status-tag"
                        color={status === CaseStatus.OPEN ? '#fa541c' : '#389e0d'}
                        full
                        value={status}
                    />

                    <div className="case-list__status__date">
                        <ICONS.ClockCircleOutlined />
                        <FormatDate date={(status === CaseStatus.OPEN ? startDate : endDate) || _createdAt} onlyFrom />
                    </div>
                </div>

                {status === CaseStatus.RESOLVED && (
                    <div className="case-list-title__closed">
                        {locale('case.list.table.description.closed')} <FormatDate date={endDate} />{' '}
                        {resolutionStatus && (
                            <>
                                {locale('case.list.table.description.as')}{' '}
                                <span className="case-list-title__resolution-button">{resolutionStatus}</span>
                            </>
                        )}
                    </div>
                )}
            </div>
        ),
    },
    '#computeDescription': {
        name: '',
        isForce: false,
        render: (description: string): ReactElement => (
            <div className="result-card__description">
                {description === 'N/A' ? locale('common.noDescription') : cutString(description, 140)}
            </div>
        ),
    },
    '#computeTags': {
        name: '',
        isForce: false,
        render: (tags: string[]): ReactElement => (
            <DisplayTag.List data={tags} limit={5}>
                {(tag) => <DisplayTag tag={tag} />}
            </DisplayTag.List>
        ),
    },
    '#computeLink': {
        name: '',
        isForce: false,
        render: ({
            parentType,
            parentData,
        }: {
            parentType: 'case' | 'alert';
            parentData: CaseOutput | AlertOutput;
        }) => (
            <Link
                className="observable-result-card__links"
                to={
                    parentType === 'case'
                        ? RouteBuilder.compute('cases.details', { caseId: parentData._id })
                        : RouteBuilder.compute('alerts.details', { alertId: parentData._id })
                }
            >
                <div>{parentType === 'case' ? <ICONS.CaseIcon /> : <ICONS.AlertIcon />}</div>
                <div>{parentData.title}</div>
            </Link>
        ),
    },
});
